import React from "react";
import {QuickReplyEditor} from "../QuickReplyEditor";
import {ImageUploader} from "../../../../components/ImageUploader";
import {LineCardEditor} from "../LineCardEditor";
import {LeftChatBubbleView} from "../../../../designSystem/components/ChatBubbleView";
import {LongMenuEditor} from "../LongMenuEditor";
import {ActionButton} from "../../../../designSystem/components/ActionButton";

const RESPONSE_TYPES = [
  { label: '文字', value: 'text' },
  { label: '圖片', value: 'image' },
  { label: '小選單', value: 'menu' },
  { label: '長選單', value: 'longMenu' },
  { label: '卡片', value: 'card' },
];

const blankCard = () => ({
  title: '',
  content: '',
  text: '',
  link: '',
  image: '',
});

export const ResponseEditor = ({ block, onChange, onRemove }) => {
  const type = block.type;

  // ---- helpers for cards ----
  const ensureCards = () => {
    // payload.cards 已是陣列 → 直接回傳
    if (Array.isArray(block.payload?.cards)) return block.payload.cards;

    // 舊版單卡結構 → 轉為陣列（相容）
    const { title, content, text, link, image } = block.payload || {};
    const hasLegacy =
      [title, content, text, link, image].some(v => typeof v === 'string' && v.length > 0);

    if (hasLegacy) {
      return [{ title: title||'', content: content||'', text: text||'', link: link||'', image: image||'' }];
    }
    // 否則回傳空陣列
    return [];
  };

  const cards = type === 'card' ? ensureCards() : [];

  const setCards = (nextCards) => {
    onChange({
      ...block,
      type: 'card',
      payload: { cards: nextCards },
    });
  };

  const addCard = () => setCards([...(cards || []), blankCard()]);
  const updateCard = (idx, patch) => {
    const next = [...cards];
    next[idx] = { ...next[idx], ...patch };
    setCards(next);
  };
  const removeCard = (idx) => {
    const next = [...cards];
    next.splice(idx, 1);
    setCards(next);
  };

  return (
    <div className="p-1 mb-3 flex flex-row justify-between">
      {type === "text" && (
        <LeftChatBubbleView>
          <textarea
            className="w-[300px] rounded p-2"
            rows={4}
            value={block.payload?.text || ""}
            onChange={e => onChange({...block, payload: {text: e.target.value}})}
            placeholder="請輸入要回覆的文字"
          />
        </LeftChatBubbleView>
      )}

      {type === "image" && (
        <LeftChatBubbleView>
          <ImageUploader
            isSquareCropper
            handleUploadedImage={url => onChange({...block, payload: {...block.payload, url}})}
            uploadFolder="robotImage"
            uploadedImageUrl={block.payload?.url}
            placeholderBgColor="bg-gray-100"
            placeholder="未上傳圖片"
            cropEnabled={false}
          />
        </LeftChatBubbleView>
      )}

      {type === "menu" && (
        <LeftChatBubbleView isNoArrow>
          <QuickReplyEditor
            items={block.payload?.items || []}
            title={block.payload?.title || ''}
            onChangeTitle={title => onChange({...block, payload: {...block.payload, title}})}
            onChangeItems={(items) => onChange({...block, payload: {...block.payload, items}})}
          />
        </LeftChatBubbleView>
      )}

      {type === "longMenu" && (
        <LongMenuEditor
          items={block.payload?.items || []}
          onChangeItems={items => onChange({ ...block, payload: {...block.payload, items }})}
        />
      )}

      {type === "card" && (
        // 卡片走馬燈（頂對齊，讓新增卡片與卡片本體同高，不受刪除鍵影響）
        <div className="flex items-start gap-3 overflow-x-auto pb-2 pr-1">
          {cards.map((c, idx) => (
            <div key={idx} className="flex flex-col items-center gap-2">
              {/* 卡片 */}
              <LeftChatBubbleView isNoArrow isNoIcon={idx > 0}>
                <div className="shrink-0 min-w-[320px]">
                  <LineCardEditor
                    title={c.title}
                    content={c.content}
                    text={c.text}
                    link={c.link}
                    image={c.image}
                    onChangeTitle={(title) => updateCard(idx, {title})}
                    onChangeContent={(content) => updateCard(idx, {content})}
                    onChangeText={(text) => updateCard(idx, {text})}
                    onChangeLink={(link) => updateCard(idx, {link})}
                    onChangeImage={(image) => updateCard(idx, {image})}
                  />
                </div>
              </LeftChatBubbleView>

              <ActionButton onClick={() => removeCard(idx)} label="刪除此卡片" variant="outline" color="warning" />
            </div>
          ))}

          <ActionButton
            label="新增卡片"
            iconName="fa-plus"
            color="secondary"
            variant="dashed"
            onClick={addCard}
          />
        </div>
      )}

      <div className="flex flex-col items-end">
        <div className="flex items-center gap-2 mb-2">
          <select
            value={type}
            onChange={e => {
              const v = e.target.value;
              onChange({
                ...block,
                type: v,
                // 若切到 card -> 預設用陣列結構；其他 → 清空
                payload: v === 'card' ? {cards: cards.length ? cards : [blankCard()]} : {},
              });
            }}
            className="border rounded px-2 py-1"
          >
            {RESPONSE_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <ActionButton onClick={onRemove} label="刪除" variant="outline" color="warning" />
      </div>
    </div>
  );
};
